import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api, fileUrl, useAuth } from "../lib/api";
import { Grid, Page } from "../components/ComicCard";

export function Library() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/me/library").then((r) => setD(r.data)); }, []);
  return (
    <Page>
      <h1 className="text-4xl sm:text-5xl font-black">Library</h1>
      {[["Continue reading", "history"], ["Purchased", "purchased"], ["Wishlist & saved", "saved"]].map(([t, k]) => (
        <section key={k} className="mt-10"><h2 className="text-xl font-bold mb-4">{t}</h2>{d?.[k]?.length ? <Grid comics={d[k]} testid={`library-${k}`} /> : <div className="jx-muted text-sm">Nothing here yet.</div>}</section>))}
    </Page>
  );
}

export function Notifications() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/notifications").then((r) => { setD(r.data); api.post("/notifications/read-all"); }); }, []);
  return (
    <Page>
      <h1 className="text-4xl sm:text-5xl font-black">Notifications</h1>
      <div className="mt-6 space-y-2" data-testid="notification-list">{d?.items.length ? d.items.map((n) => (
        <Link key={n.id} to={n.link || "#"} className={`block jx-surface rounded-xl px-4 py-3 ${!n.read ? "border-[var(--jx-pink)]" : ""}`}><span className="jx-tag jx-mint mr-2">{n.kind}</span>{n.text}<div className="text-xs jx-muted mt-1">{n.created_at.slice(0, 16).replace("T", " ")}</div></Link>)) : <div className="jx-muted">You're all caught up.</div>}</div>
    </Page>
  );
}

export function Creators() {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => { const t = setTimeout(() => api.get("/users", { params: { q } }).then((r) => setList(r.data)), 250); return () => clearTimeout(t); }, [q]);
  return (
    <Page>
      <h1 className="text-4xl sm:text-5xl font-black">Creators</h1>
      <input data-testid="creator-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members" className="w-full mt-6 jx-surface rounded-full px-5 h-12" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-6" data-testid="creator-list">{list.map((u) => (
        <Link key={u.id} to={`/u/${u.username}`} data-testid={`creator-${u.username}`} className="jx-surface rounded-2xl p-4 text-center hover:border-[var(--jx-pink)] transition-colors">
          <img src={fileUrl(u.avatar_url) || "/icon-192.png"} alt="" className="w-16 h-16 rounded-full mx-auto object-cover" />
          <div className="font-bold mt-2 truncate">@{u.username}</div><div className="jx-tag jx-mint">Member #{u.member_number}</div>
          <div className="text-xs jx-muted mt-1">{u.followers_count} followers</div>{u.is_boosted && <span className="jx-tag jx-promo px-2 rounded">Promoted</span>}
        </Link>))}</div>
    </Page>
  );
}

export function Policy() {
  return (
    <Page className="max-w-3xl">
      <h1 className="text-4xl font-black">Community guidelines &amp; policies</h1>
      {[["Ad-free promise", "Jixxeo Comics shows no advertisements of any kind. Revenue comes from subscriptions, comic sales, tips and clearly labeled “Promoted” boosts."],
        ["Content & copyright", "Only upload work you own or have rights to. AI-generated comics are labeled. Rights holders can file a copyright report from any comic page; reported content is reviewed and may be taken down. Suspended members may submit an appeal."],
        ["Respect", "No harassment, hate, spam, or sexual content involving minors. Mature content is hidden unless enabled in settings. You can block and report any member, comment or comic."],
        ["Earnings & payouts", "Minimum cash-out $5.00, fee $0.10 per successful cash-out. Only funded balances (eligible rewards from funded campaigns, sales share, tips) are withdrawable. XP, badges and subscription value are never cash. Exact internal reward rates are confidential; rate changes apply prospectively and never remove accrued earnings. Fraudulent engagement is excluded."],
        ["Fair play", "Fake views, automated likes, multi-account voting and tip manipulation are detected and may lead to suspension and forfeiture of affected rewards."],
        ["Privacy", "Earnings are private to you. You can delete your account at any time from Settings."]].map(([t, b]) => <section key={t} className="mt-8"><h2 className="text-xl font-bold">{t}</h2><p className="jx-muted mt-2">{b}</p></section>)}
    </Page>
  );
}

export function PaymentResult() {
  const { result } = useParams();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [st, setSt] = useState(result === "success" ? "checking" : "cancel");
  useEffect(() => {
    if (result !== "success") return;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    let n = 0;
    const poll = async () => {
      try { const { data } = await api.get(`/payments/status/${sid}`); if (data.payment_status === "paid") { setSt("paid"); refresh(); return; } if (["expired", "failed"].includes(data.payment_status)) return setSt("failed"); } catch { return setSt("failed"); }
      if (++n < 10) setTimeout(poll, 2000); else setSt("pending");
    };
    poll();
  }, [result, refresh]);
  const view = { checking: [Loader2, "Confirming your payment…", "jx-muted animate-spin"], paid: [CheckCircle2, "Payment confirmed — thank you!", "jx-mint"], pending: [Loader2, "Payment still processing. We'll notify you when it completes.", "jx-gold"], failed: [XCircle, "Payment failed or expired. You were not charged.", "text-red-400"], cancel: [XCircle, "Checkout cancelled. You were not charged.", "jx-muted"] }[st];
  const [I, msg, cls] = view;
  return <Page><div data-testid={`payment-${st}`} className="max-w-md mx-auto text-center py-20"><I size={56} className={`mx-auto ${cls}`} /><h1 className="text-2xl font-bold mt-4">{msg}</h1><button data-testid="payment-continue-btn" onClick={() => nav("/")} className="jx-btn px-6 py-3 mt-6">Continue</button></div></Page>;
}
